import { useCallback } from 'react';
import { toPng } from 'html-to-image';
import { useToast } from '@chatsdet/ui';

/**
 * Hook that exports the flow canvas content as a PNG image.
 * Queries [data-flow-canvas] to find the scrollable container,
 * targets its first child (the content div with the zoom transform),
 * and triggers a browser download.
 */
export function useExportFlowAsPng(workflowName: string): () => void {
  const { toast } = useToast();

  return useCallback(async () => {
    const container = document.querySelector('[data-flow-canvas]');
    if (!container) {
      toast.error('Could not find flow canvas');
      return;
    }

    // Target the content div (first child) which has the zoom transform
    const contentEl = container.firstElementChild as HTMLElement | null;
    if (!contentEl) {
      toast.error('Could not find flow content');
      return;
    }

    try {
      // Resolve the page background color so the PNG isn't transparent.
      // The canvas has a semi-transparent bg-muted/20 tint which composites
      // on top of the page background, so we use the body's computed color.
      const pageBg = getComputedStyle(document.body).backgroundColor || '#ffffff';

      const dataUrl = await toPng(contentEl, {
        pixelRatio: 2,
        backgroundColor: pageBg,
        style: { transform: 'none' },
      });

      // Trigger download
      const link = document.createElement('a');
      link.download = `${workflowName.replace(/[^a-zA-Z0-9-_ ]/g, '')}.png`;
      link.href = dataUrl;
      link.click();

      toast.success('Flow exported as PNG');
    } catch (err) {
      console.error('PNG export failed:', err);
      toast.error('Could not generate PNG');
    }
  }, [workflowName, toast]);
}
